import {
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { AddTransactionForm } from "../components/AddTransactionForm";
import { sliceFillColor } from "../components/ExpensePieChart";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
  apiListAccounts,
  apiListCategories,
  apiListTransactions,
  apiSummary,
  type AccountRow,
  type CategoryRow,
  type SummaryResponse,
  type TransactionRow,
} from "../api";
import { accountTypeLabel, compareAccountTypes } from "../accountTypes";
import {
  readIncludeOffBudgetFromStorage,
  writeIncludeOffBudgetToStorage,
} from "../includeOffBudgetPreference";
import { currentMonthISO, formatCents, formatMonthLongYear, monthRange } from "../utils/money";

type CategorySummary = SummaryResponse["byCategory"][number];

function OverviewOffBudgetChip({
  includeOffBudget,
  onToggle,
  standalone,
  hint,
}: {
  includeOffBudget: boolean;
  onToggle: () => void;
  standalone: boolean;
  hint: ReactNode;
}) {
  return (
    <div className={`overview-off-budget-chip${standalone ? " overview-off-budget-chip-standalone" : ""}`}>
      <div className="overview-off-budget-chip-text">
        <span className="overview-off-budget-chip-label">Off Budget in monthly net</span>
        <p className="muted small mb-0">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={includeOffBudget}
        aria-label={
          includeOffBudget ? "Including Off Budget in monthly net" : "Excluding Off Budget from monthly net"
        }
        className={`toggle-switch${includeOffBudget ? " toggle-switch-on" : ""}`}
        onClick={onToggle}
      >
        <span className="toggle-thumb" aria-hidden />
      </button>
    </div>
  );
}

function totalsByAccountType(accts: AccountRow[]): { type: string; subtotalCents: number }[] {
  const map = new Map<string, number>();
  for (const a of accts) {
    map.set(a.type, (map.get(a.type) ?? 0) + a.balanceCents);
  }
  return [...map.keys()]
    .sort(compareAccountTypes)
    .map((type) => ({ type, subtotalCents: map.get(type)! }));
}

export function OverviewPage() {
  const [month, setMonth] = useState(currentMonthISO);
  const [includeOffBudget, setIncludeOffBudget] = useState(readIncludeOffBudgetFromStorage);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [recent, setRecent] = useState<TransactionRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [flyoutCategory, setFlyoutCategory] = useState<CategorySummary | null>(null);
  const [flyoutTxs, setFlyoutTxs] = useState<TransactionRow[]>([]);
  const [flyoutLoading, setFlyoutLoading] = useState(false);
  const compactLists = useMediaQuery("(max-width: 640px)");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = monthRange(month);
      const [sum, txs, ac, cat] = await Promise.all([
        apiSummary(month, { excludeOffBudget: !includeOffBudget }),
        apiListTransactions({ from, to }),
        apiListAccounts(),
        apiListCategories(),
      ]);
      setSummary(sum);
      setRecent(txs.transactions.slice(0, 8));
      setAccounts(ac.accounts);
      setCategories(cat.categories);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [month, includeOffBudget]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    writeIncludeOffBudgetToStorage(includeOffBudget);
  }, [includeOffBudget]);

  useEffect(() => {
    if (!flyoutCategory) {
      setFlyoutTxs([]);
      return;
    }
    let cancelled = false;
    setFlyoutLoading(true);
    const { from, to } = monthRange(month);
    void apiListTransactions({ from, to, categoryId: flyoutCategory.categoryId })
      .then((res) => {
        if (!cancelled) setFlyoutTxs(res.transactions);
      })
      .catch(() => {
        if (!cancelled) setFlyoutTxs([]);
      })
      .finally(() => {
        if (!cancelled) setFlyoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flyoutCategory, month]);

  useEffect(() => {
    if (!flyoutCategory) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFlyoutCategory(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flyoutCategory]);

  useEffect(() => {
    if (flyoutCategory) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [flyoutCategory]);

  const balancesByType = (() => {
    const rows = totalsByAccountType(accounts);
    if (includeOffBudget) return rows;
    return rows.filter((r) => r.type !== "off_budget");
  })();

  function openCategoryFlyout(r: CategorySummary) {
    setFlyoutCategory(r);
  }

  function categoryRowKeyHandlers(r: CategorySummary) {
    return (e: ReactKeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openCategoryFlyout(r);
      }
    };
  }

  if (loading && !summary) {
    return (
      <p className="muted" style={{ padding: "1rem 0" }}>
        Loading overview…
      </p>
    );
  }

  const hasSetup = accounts.length > 0 && categories.length > 0;

  return (
    <div className="stack">
      {error ? <p className="error">{error}</p> : null}

      <div className="card row-between overview-top">
        <div>
          <h2 className="mt-0">Overview</h2>
          <p className="muted mb-0">Month summary and recent activity.</p>
        </div>
        <input
          type="month"
          className="input-compact overview-month-input"
          value={month}
          onChange={(e) => setMonth(e.currentTarget.value)}
          aria-label="Month"
          title={formatMonthLongYear(month)}
        />
      </div>

      {!hasSetup ? (
        <>
          <div className="card card-warn">
            <strong>Get started</strong>
            <p className="muted mb-0">
              Add at least one <Link to="/accounts">account</Link> and one <Link to="/categories">category</Link>, then
              record <Link to="/transactions">transactions</Link> or use the form below once ready.
            </p>
          </div>
          <div className="card">
            <OverviewOffBudgetChip
              includeOffBudget={includeOffBudget}
              onToggle={() => setIncludeOffBudget((v) => !v)}
              standalone
              hint={
                <>
                  Same toggle as <Link to="/reports">Reports</Link>. Applies once you have data.
                </>
              }
            />
          </div>
        </>
      ) : (
        <div className="card overview-quick-card">
          <AddTransactionForm
            accounts={accounts}
            categories={categories}
            onCreated={() => void load()}
            showDate={false}
            title="Quick add transaction"
            hint="Uses today’s date. Recent activity below only lists transactions in the month selected above—change the month if you don’t see a new entry yet."
            compactShell
          />
          <OverviewOffBudgetChip
            includeOffBudget={includeOffBudget}
            onToggle={() => setIncludeOffBudget((v) => !v)}
            standalone={false}
            hint={
              <>
                Same toggle as <Link to="/reports">Reports</Link>. Off hides Off Budget from net and category totals.
              </>
            }
          />
        </div>
      )}

      {summary ? (
        <>
          <div className="grid3">
            <div className="card stat">
              <div className="muted small">Total monthly income</div>
              <div className="stat-value ok">{formatCents(summary.totalIncomeCents)}</div>
            </div>
            <div className="card stat">
              <div className="muted small">Total monthly expenses</div>
              <div className="stat-value danger">{formatCents(summary.totalExpenseCents)}</div>
            </div>
            <div className="card stat">
              <div className="muted small">Monthly net</div>
              <div className={`stat-value${summary.netCents < 0 ? " danger" : ""}`}>
                {formatCents(summary.netCents)}
              </div>
            </div>
          </div>

          <div className="card overview-balances">
            <h3 className="mt-0">All-time balances</h3>
            <p className="muted small mt-0 mb-0">
              Total balance by account type (lifetime ledger). Manage accounts on{" "}
              <Link to="/accounts">Accounts</Link>.
            </p>
            {accounts.length === 0 ? (
              <p className="muted small mb-0">No accounts yet.</p>
            ) : (
              <div className="overview-type-totals">
                {balancesByType.map(({ type, subtotalCents }) => (
                  <div key={type} className="overview-savings-row">
                    <span className="muted">{accountTypeLabel(type)}</span>
                    <span className={`mono overview-balance-value ${subtotalCents >= 0 ? "amt-in" : "amt-out"}`}>
                      {formatCents(subtotalCents)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}

      {summary && summary.byCategory.length > 0 ? (
        <div className="card">
          <h3 className="mt-0">By category</h3>
          <p className="muted small mb-0">Click a category to see this month&apos;s transactions.</p>
          {compactLists ? (
            <div className="overview-by-cat-stack">
              {summary.byCategory.map((r, i) => (
                <button
                  key={r.categoryId}
                  type="button"
                  className="overview-by-cat-card"
                  onClick={() => openCategoryFlyout(r)}
                >
                  <span
                    className="pie-legend-swatch"
                    style={{ background: sliceFillColor(r.color ?? null, i) }}
                    aria-hidden
                  />
                  <span className="overview-by-cat-card-name">{r.name}</span>
                  <span className={"badge " + (r.kind === "income" ? "badge-in" : "badge-out")}>{r.kind}</span>
                  <span className="mono overview-by-cat-card-amt">{formatCents(r.totalCents)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Kind</th>
                    <th className="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byCategory.map((r, i) => (
                    <tr
                      key={r.categoryId}
                      className="overview-category-row"
                      tabIndex={0}
                      onClick={() => openCategoryFlyout(r)}
                      onKeyDown={categoryRowKeyHandlers(r)}
                      aria-label={`View transactions for ${r.name}`}
                    >
                      <td>
                        <span
                          className="pie-legend-swatch"
                          style={{ background: sliceFillColor(r.color ?? null, i) }}
                          aria-hidden
                        />{" "}
                        {r.name}
                      </td>
                      <td>
                        <span className={"badge " + (r.kind === "income" ? "badge-in" : "badge-out")}>{r.kind}</span>
                      </td>
                      <td className="right mono">{formatCents(r.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <div className="card">
        <h3 className="mt-0">Recent transactions</h3>
        {recent.length === 0 ? (
          <p className="muted mb-0">No transactions this month yet.</p>
        ) : compactLists ? (
          <div className="recent-txn-stack">
            {recent.map((t) => (
              <div key={t.id} className="recent-txn-card">
                <div className="recent-txn-card-top">
                  <span className="mono small muted">{t.occurred_on}</span>
                  <span className={`mono recent-txn-amt ${t.amount_cents >= 0 ? "amt-in" : "amt-out"}`}>
                    {formatCents(t.amount_cents)}
                  </span>
                </div>
                <div className="recent-txn-desc">{t.description || "—"}</div>
                <div className="recent-txn-meta muted small">
                  {t.category_name ? <span>{t.category_name}</span> : null}
                  {t.category_name && t.account_name ? <span aria-hidden> · </span> : null}
                  <span>{t.account_name}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Account</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((t) => (
                  <tr key={t.id}>
                    <td className="mono">{t.occurred_on}</td>
                    <td>{t.description || "—"}</td>
                    <td>{t.account_name}</td>
                    <td className={`right mono ${t.amount_cents >= 0 ? "amt-in" : "amt-out"}`}>
                      {formatCents(t.amount_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {flyoutCategory ? (
        <div className="flyout-backdrop" role="presentation" onClick={() => setFlyoutCategory(null)}>
          <div
            className="flyout-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="overview-cat-flyout-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flyout-header">
              <h3 id="overview-cat-flyout-title" className="mt-0">
                {flyoutCategory.name}
              </h3>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setFlyoutCategory(null)}>
                Close
              </button>
            </div>
            <p className="muted small mb-0">
              {formatMonthLongYear(month)} ·{" "}
              <span className={"badge " + (flyoutCategory.kind === "income" ? "badge-in" : "badge-out")}>
                {flyoutCategory.kind}
              </span>{" "}
              · total {formatCents(flyoutCategory.totalCents)}
            </p>
            <div className="flyout-body">
              {flyoutLoading ? (
                <p className="muted small">Loading…</p>
              ) : flyoutTxs.length === 0 ? (
                <p className="muted small mb-0">No transactions this month.</p>
              ) : (
                <div className="flyout-txn-stack">
                  {flyoutTxs.map((t) => (
                    <div key={t.id} className="flyout-txn-row">
                      <div className="flyout-txn-row-top">
                        <span className="mono small muted">{t.occurred_on}</span>
                        <span className={`mono flyout-txn-amt ${t.amount_cents >= 0 ? "amt-in" : "amt-out"}`}>
                          {formatCents(t.amount_cents)}
                        </span>
                      </div>
                      <div className="flyout-txn-desc">{t.description || "—"}</div>
                      <div className="muted small">{t.account_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="muted small mb-0">
              <Link to="/transactions" onClick={() => setFlyoutCategory(null)}>
                All transactions
              </Link>{" "}
              for edits and filters.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
